import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, IconButton, Slide, Tooltip } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';

import {
    AREA_CELL_BG,
    AREA_LINE_ICON_PATHS,
    MORE_ICON_PATH,
    areaLetterMarks,
    buildPhoneAreaRow,
    builtInInkVar,
    inkWithContrast,
    isBuiltInArea,
    SELECTED_TINT_ALPHA,
    selectedCellBackground,
    tint,
    type TodayAreaOption,
} from '../utils/todayAreaRow';

export type InkVars = React.CSSProperties & { '--area-ink-dark'?: string; '--area-ink-light'?: string };

const SlideUp = React.forwardRef(function SlideUp(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/** An area's ink per theme, for `.today-area-ink` and `.today-area-fill` (index.css). */
export function inkVars(option: TodayAreaOption): InkVars {
    if (option.id === 'all' || isBuiltInArea(option.id)) {
        const v = builtInInkVar(option.id);
        return { '--area-ink-dark': v, '--area-ink-light': v };
    }
    return {
        '--area-ink-dark': inkWithContrast(option.color, AREA_CELL_BG.dark),
        '--area-ink-light': inkWithContrast(option.color, AREA_CELL_BG.light),
    };
}

function selectedTint(option: TodayAreaOption): string {
    return option.id === 'all'
        ? `rgba(var(--color-accent-rgb), ${SELECTED_TINT_ALPHA})`
        : tint(option.color, SELECTED_TINT_ALPHA);
}

/** A built-in area's line icon, or the letters of an area you made, in `currentColor`. */
function AreaMark({ option, letters, size }: { option: TodayAreaOption; letters?: string; size: number }) {
    const path = AREA_LINE_ICON_PATHS[option.id];
    if (path) {
        return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d={path} />
            </svg>
        );
    }
    const text = letters || '?';
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-area-letters={text}>
            <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth={1.6} />
            <text x="12" y="12.5" dominantBaseline="central" textAnchor="middle" fontSize={text.length > 1 ? 9 : 11} fontWeight={700} fill="currentColor" style={{ fontFamily: 'inherit' }}>
                {text}
            </text>
        </svg>
    );
}

function MoreDots({ size }: { size: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d={MORE_ICON_PATH} />
        </svg>
    );
}

interface TodayAreaRowProps {
    /** All areas first, then every area in display order. */
    options: TodayAreaOption[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    /** At 1024 px and up: one compact row of every area. Below: equal cells and More. */
    isDesktop: boolean;
    /** Shown after the row ("Use profile mode"). */
    extra?: React.ReactNode;
}

/**
 * Today's area row (today-phone-area-row). Built-in areas are line icons in their ink token,
 * areas you made are letter marks; the selected cell takes its area's tint and an outline.
 */
const TodayAreaRow: React.FC<TodayAreaRowProps> = ({ options, selectedId, onSelect, isDesktop, extra }) => {
    const [sheetOpen, setSheetOpen] = useState(false);
    const letters = useMemo(() => areaLetterMarks(options), [options]);
    const cells = useMemo(() => buildPhoneAreaRow(options, selectedId), [options, selectedId]);

    useEffect(() => {
        if (isDesktop) setSheetOpen(false);
    }, [isDesktop]);

    const cellStyle = (option: TodayAreaOption, selected: boolean, rest?: string): InkVars => ({
        ...inkVars(option),
        ...(selected ? selectedCellBackground(selectedTint(option)) : { backgroundColor: rest }),
        boxShadow: selected ? 'inset 0 0 0 1.5px currentColor' : undefined,
    });

    if (isDesktop) {
        return (
            <div
                data-testid="today-area-row"
                role="group"
                aria-label="Life areas"
                className="flex shrink-0 items-center gap-1 p-1 rounded-xl"
                style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
            >
                {options.map((option) => {
                    const selected = selectedId === option.id;
                    return (
                        <Tooltip key={option.id} title={option.label}>
                            <button
                                type="button"
                                data-testid="today-area-cell"
                                data-area-id={option.id}
                                aria-label={option.label}
                                aria-pressed={selected}
                                onClick={() => onSelect(option.id)}
                                className={`today-area-ink shrink-0 h-8 w-8 flex items-center justify-center rounded-lg transition-colors ${selected ? '' : 'hover:bg-theme-card-hover'}`}
                                style={cellStyle(option, selected)}
                            >
                                <AreaMark option={option} letters={letters[option.id]} size={18} />
                            </button>
                        </Tooltip>
                    );
                })}
                {extra}
            </div>
        );
    }

    return (
        <div className="w-full min-w-0">
            <div
                data-testid="today-area-row"
                role="group"
                aria-label="Life areas"
                className="grid gap-2 w-full"
                style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}
            >
                {cells.map((cell) => {
                    if (cell.kind === 'area') {
                        const { option } = cell;
                        const selected = selectedId === option.id;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                data-testid="today-area-cell"
                                data-area-id={option.id}
                                aria-label={option.label}
                                aria-pressed={selected}
                                onClick={() => onSelect(option.id)}
                                className="today-area-ink h-[52px] min-w-0 flex items-center justify-center rounded-xl transition-colors"
                                style={cellStyle(option, selected, 'var(--color-bg-tertiary)')}
                            >
                                <AreaMark option={option} letters={letters[option.id]} size={22} />
                            </button>
                        );
                    }
                    const shown = cell.selectedHidden;
                    return (
                        <button
                            key="more"
                            type="button"
                            data-testid="today-area-more"
                            aria-haspopup="dialog"
                            aria-label={shown ? `${shown.label}. More areas` : `More areas, +${cell.hidden.length}`}
                            aria-pressed={Boolean(shown)}
                            onClick={() => setSheetOpen(true)}
                            className={`${shown ? 'today-area-ink' : 'text-theme-secondary'} h-[52px] min-w-0 flex items-center justify-center gap-1 rounded-xl transition-colors`}
                            style={shown ? cellStyle(shown, true, 'var(--color-bg-tertiary)') : { backgroundColor: 'var(--color-bg-tertiary)' }}
                        >
                            {shown ? (
                                <AreaMark option={shown} letters={letters[shown.id]} size={22} />
                            ) : (
                                <>
                                    <MoreDots size={18} />
                                    <span className="text-xs font-bold">+{cell.hidden.length}</span>
                                </>
                            )}
                        </button>
                    );
                })}
            </div>
            {extra && <div className="mt-2 flex justify-end">{extra}</div>}

            <Dialog
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                TransitionComponent={SlideUp}
                fullWidth
                aria-labelledby="today-areas-sheet-title"
                sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' } }}
                PaperProps={{
                    'data-testid': 'today-areas-sheet',
                    sx: {
                        m: 0,
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: '16px 16px 0 0',
                        backgroundColor: 'var(--color-bg-secondary)',
                        backgroundImage: 'none',
                        color: 'var(--color-text-primary)',
                    },
                } as any}
            >
                <div className="flex items-center gap-2 px-4 pt-4 pb-2">
                    <h2 id="today-areas-sheet-title" className="text-lg font-semibold m-0 flex-1">
                        Life areas
                    </h2>
                    <IconButton aria-label="Close" onClick={() => setSheetOpen(false)} sx={{ color: 'var(--color-text-secondary)' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
                <div className="flex flex-col gap-1.5 px-3 pb-5">
                    {options.map((option) => {
                        const selected = selectedId === option.id;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                data-testid="today-areas-sheet-option"
                                data-area-id={option.id}
                                aria-pressed={selected}
                                onClick={() => {
                                    onSelect(option.id);
                                    setSheetOpen(false);
                                }}
                                className="today-area-ink flex items-center gap-3 min-h-12 px-3.5 py-2.5 rounded-xl text-left"
                                style={{ ...inkVars(option), backgroundColor: selected ? 'var(--color-bg-tertiary)' : 'transparent' }}
                            >
                                <AreaMark option={option} letters={letters[option.id]} size={22} />
                                <span className="flex-1 min-w-0 text-[15px] font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                                    {option.label}
                                </span>
                                {selected && <CheckIcon fontSize="small" aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>
            </Dialog>
        </div>
    );
};

export default TodayAreaRow;
